import { useUpdateEffect } from '@lbcamden/lib-react';
import {
  AuthenticatorAssertionResponseJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/types';
import { argon2id, argon2Verify } from 'hash-wasm';
import useLocalStorage from 'use-local-storage-state';

export const getCredentialsString = ({
  authenticatorData,
  userHandle,
}: AuthenticatorAssertionResponseJSON) =>
  JSON.stringify([authenticatorData.slice(0, 48), userHandle]);

export const useClientAuthInfo = () => {
  const [authOpts, storeAuthOpts] =
    useLocalStorage<PublicKeyCredentialRequestOptionsJSON>('authOpts');

  const [hashedCredentials, storeHashedCredentials, { removeItem: deleteHashedCredentials }] =
    useLocalStorage<string>('hashedCredentials');

  const [username, storeUsername] = useLocalStorage<string>('username');

  const hashAndStoreAuthResp = async (value: AuthenticatorAssertionResponseJSON) => {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const password = getCredentialsString(value);

    // https://tobtu.com/minimum-password-settings/
    // bcrypt cannot be used due to the string size (has a max of 72 bytes)
    const hash = await argon2id({
      salt,
      password,
      parallelism: 1,
      iterations: 4,
      memorySize: 9216,
      hashLength: 32,
      outputType: 'encoded',
    });

    storeHashedCredentials(hash);
  };

  const verifyAuthResp = async (value: AuthenticatorAssertionResponseJSON) => {
    const password = getCredentialsString(value);

    const isValid =
      !!hashedCredentials &&
      (await argon2Verify({
        password,
        hash: hashedCredentials,
      }));

    if (!isValid) {
      throw Error('Invalid response');
    }
  };

  useUpdateEffect(deleteHashedCredentials, [username]);

  return {
    authOpts,
    storeAuthOpts,
    username,
    storeUsername,
    hashAndStoreAuthResp,
    verifyAuthResp,
    hasAuthResp: !!hashedCredentials,
  };
};
